import React, { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { useTz, TIMEZONE_OPTIONS } from "@/lib/TzContext";
import { useI18n } from "@/lib/I18nContext";

export default function TimezonePicker() {
  const { tz, setTz, tzLabel } = useTz();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const shortLabel = (tzLabel || "").split(" (")[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="tz-picker-toggle"
        title={tzLabel}
        className="h-10 md:h-11 px-2 md:px-3 border border-zinc-800 hover:border-[var(--c-lime)] transition-colors flex items-center gap-1.5 text-zinc-300 hover:text-white"
      >
        <Globe size={16} />
        <span className="hidden sm:inline text-[10px] uppercase tracking-widest font-mono-data truncate max-w-[120px]">
          {shortLabel}
        </span>
      </button>
      {open && (
        <div
          data-testid="tz-picker-menu"
          className="bg-[#0A0A0A] border border-zinc-800 shadow-2xl z-50
                     fixed inset-x-2 top-[60px]
                     sm:absolute sm:inset-x-auto sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-72"
        >
          <div className="px-4 py-3 border-b border-zinc-800">
            <div className="font-display text-sm uppercase">{t("nav.timezone")}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mt-0.5">
              {t("nav.timezone.help")}
            </div>
          </div>
          <ul role="listbox" className="max-h-72 overflow-y-auto">
            {TIMEZONE_OPTIONS.map((opt) => {
              const active = opt.id === tz;
              return (
                <li key={opt.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-testid={`tz-option-${opt.id.replace(/\//g, "_")}`}
                    onClick={() => { setTz(opt.id); setOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-xs flex items-center justify-between gap-3 transition-colors ${
                      active
                        ? "bg-[var(--c-lime)] text-black font-bold"
                        : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
                    }`}
                  >
                    <span className="truncate">{opt.label}</span>
                    {active && <Check size={14} className="shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
